/**
 * AddEditEcosystemEntityDialog
 * Dialog for creating/editing ecosystem entities (organizations, platforms, alliances, etc.)
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateEcosystemEntity, useUpdateEcosystemEntity, useEcosystemOrganizations } from '@/hooks/useEcosystemData';
import { ORG_KIND_LABELS, PRIORITY_TIER_LABELS, type OrgKind, type PriorityTier } from '@/types/ecosystem';

interface AddEditEcosystemEntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity?: any | null;
  defaultParentId?: string | null;
}

export function AddEditEcosystemEntityDialog({
  open,
  onOpenChange,
  entity,
  defaultParentId,
}: AddEditEcosystemEntityDialogProps) {
  const isEditMode = !!entity;

  const [displayName, setDisplayName] = useState('');
  const [orgKind, setOrgKind] = useState<string>('training_provider');
  const [priorityTier, setPriorityTier] = useState<string>('');
  const [coverage, setCoverage] = useState('');
  const [parentProviderId, setParentProviderId] = useState<string>('');
  const [canonicalDomain, setCanonicalDomain] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [description, setDescription] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [stableKey, setStableKey] = useState('');
  const [providerType, setProviderType] = useState('educator');

  const createEntity = useCreateEcosystemEntity();
  const updateEntity = useUpdateEcosystemEntity();
  const { data: allEntities } = useEcosystemOrganizations();

  // Generate stable_key preview
  const autoStableKey = useMemo(() => {
    if (!displayName.trim()) return '';
    const slug = displayName.toLowerCase().replace(/['']/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (orgKind === 'platform') return `platform:${slug}`;
    if (['alliance', 'association', 'state_association', 'standards_body', 'educator_community', 'distributor', 'trade_show_producer', 'supplier_association'].includes(orgKind)) return `org:${slug}`;
    if (canonicalDomain) return `domain:${canonicalDomain.replace(/^https?:\/\//, '').replace(/\/$/, '')}`;
    if (orgKind === 'publisher') return `creator:${slug}`;
    return `provider:${slug}`;
  }, [displayName, orgKind, canonicalDomain]);

  useEffect(() => {
    if (open && entity) {
      setDisplayName(entity.display_name || '');
      setOrgKind(entity.org_kind || 'training_provider');
      setPriorityTier(entity.priority_tier?.toString() || '');
      setCoverage(entity.coverage || '');
      setParentProviderId(entity.parent_provider_id || '');
      setCanonicalDomain(entity.canonical_domain || '');
      setWebsiteUrl(entity.website_url || '');
      setDescription(entity.description || '');
      setLogoUrl(entity.logo_url || '');
      setStableKey(entity.stable_key || '');
      setProviderType(entity.provider_type || 'educator');
    } else if (open) {
      setDisplayName('');
      setOrgKind('training_provider');
      setPriorityTier('');
      setCoverage('');
      setParentProviderId(defaultParentId || '');
      setCanonicalDomain('');
      setWebsiteUrl('');
      setDescription('');
      setLogoUrl('');
      setStableKey('');
      setProviderType('educator');
    }
  }, [open, entity, defaultParentId]);

  const handleSubmit = async () => {
    if (!displayName.trim()) {
      toast.error('Display name is required');
      return;
    }

    const entityData: Record<string, unknown> = {
      display_name: displayName.trim(),
      org_kind: orgKind || null,
      priority_tier: priorityTier ? Number(priorityTier) : null,
      coverage: coverage.trim() || null,
      canonical_domain: canonicalDomain.trim() || null,
      website_url: websiteUrl.trim() || null,
      description: description.trim() || null,
      logo_url: logoUrl.trim() || null,
      stable_key: stableKey.trim() || null,
      provider_type: providerType,
    };

    // On create, use parent_id for entity_relationships row (not parent_provider_id)
    if (!isEditMode && parentProviderId) {
      entityData.parent_id = parentProviderId;
    }

    try {
      if (isEditMode) {
        await updateEntity.mutateAsync({
          providerId: entity.id,
          data: entityData,
        });
        toast.success('Entity updated');
      } else {
        await createEntity.mutateAsync({ data: entityData });
        toast.success('Entity created');
      }
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to save entity');
    }
  };

  const isSubmitting = createEntity.isPending || updateEntity.isPending;
  const parentOptions = (allEntities || []).filter((e: any) => e.id !== entity?.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Ecosystem Entity' : 'Add Ecosystem Entity'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? 'Update this organization, platform, or ecosystem entity.' : 'Add a new organization, platform, alliance, or provider to the ecosystem.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="displayName">Display Name *</Label>
            <Input id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g., Auto Care Alliance" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Organization Type</Label>
              <Select value={orgKind} onValueChange={setOrgKind}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.entries(ORG_KIND_LABELS) as [OrgKind, string][]).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Priority Tier</Label>
              <Select value={priorityTier || 'none'} onValueChange={(v) => setPriorityTier(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select tier" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No tier</SelectItem>
                  {([1, 2, 3] as PriorityTier[]).map(t => (
                    <SelectItem key={t} value={t.toString()}>{PRIORITY_TIER_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!isEditMode && (
            <div className="grid gap-2">
              <Label>Initial Placement (optional)</Label>
              <Select value={parentProviderId || 'none'} onValueChange={(v) => setParentProviderId(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="None (root entity)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (root entity)</SelectItem>
                  {parentOptions.map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Creates a relationship row. Manage all links from the detail panel after creation.</p>
            </div>
          )}
          {isEditMode && (
            <p className="text-xs text-muted-foreground border rounded-md p-2 bg-muted/50">
              Manage parent/child relationships from the detail panel's Relationships section.
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="coverage">Coverage</Label>
              <Input id="coverage" value={coverage} onChange={(e) => setCoverage(e.target.value)} placeholder="e.g., national, regional" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="canonicalDomain">Canonical Domain</Label>
              <Input id="canonicalDomain" value={canonicalDomain} onChange={(e) => setCanonicalDomain(e.target.value)} placeholder="e.g., autocare.org" />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="websiteUrl">Website URL</Label>
            <Input id="websiteUrl" type="url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://..." />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description..." rows={2} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="logoUrl">Logo URL</Label>
            <Input id="logoUrl" type="url" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://..." />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="stableKey">Stable Key</Label>
            <Input
              id="stableKey"
              value={stableKey || autoStableKey}
              onChange={(e) => setStableKey(e.target.value)}
              placeholder="Auto-generated"
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">Auto-generated from name + type. Override if needed.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEditMode ? 'Save Changes' : 'Create Entity'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
